import React from "react";
import { dateUtility } from "../../../../common/infrastructure/utilities/dateUtility";
import {
  Default_Instructor_Color,
  generateHexFromString,
  vividForDarkBackground,
} from "../../../../common/infrastructure/utilities/colorUtility";
import { IPlannerDayModel } from "../models/plannerDayModel";
import { IPlannerEventModel } from "../models/plannerEventModel";
import { IPlannerFiltersState } from "../models/plannerFilters";
import { filterEventsByFilters } from "./plannerFilterUtils";
import { EventType } from "../../../../common/enums";
import styles from "../Planner.module.scss";

// The dots stack in a column beside the day number. Four plus the "+N" label for
// the rest keep the column short and quiet; four is also what fits the tile.
export const MAX_INSTRUCTOR_DOTS = 4;

// Classes nobody has been assigned to share one neutral dot, so a day planned
// ahead without instructors doesn't look empty on the month view.
export const UNASSIGNED_DOT_NAME = "Без инструктор";
export const UNASSIGNED_DOT_COLOR = "#a8abb3";

export interface TileContentProps {
  date: Date;
  view: string;
}

export interface IInstructorDot {
  /** null for the shared "no instructor" dot. */
  employeeId: number | null;
  name: string;
  color: string;
}

export interface IInstructorDotsForDay {
  /** The first MAX_INSTRUCTOR_DOTS dots of the day: instructors by their first class, then "no instructor". */
  dots: IInstructorDot[];
  /** The rest, in the same order; shown only as "+N". */
  hidden: IInstructorDot[];
}

// Only classes that actually take place: blocked time isn't a class, and deleted
// or "will not take place" events don't happen.
const isTakingPlace = (event: IPlannerEventModel) =>
  event.type !== EventType.BlockedTime &&
  !event.isCancelled &&
  !event.willNotTakePlace;

export const getInstructorDotsForDay = (
  events: IPlannerEventModel[] | undefined
): IInstructorDotsForDay => {
  const dotsByEmployee = new Map<number, IInstructorDot>();
  let hasUnassigned = false;

  // Walk the day in start-time order, so each instructor lands where their first
  // class is. The sort is stable: classes starting together keep the schedule's order.
  const classes = (events ?? [])
    .filter(isTakingPlace)
    .sort((a, b) => a.startTime.localeCompare(b.startTime));

  classes.forEach((event) => {
    if (!event.employeeId) {
      hasUnassigned = true;
      return;
    }

    if (dotsByEmployee.has(event.employeeId)) {
      return;
    }

    dotsByEmployee.set(event.employeeId, {
      employeeId: event.employeeId,
      name: event.employee,
      // Same hue as the instructor tag on the schedule items, kept vivid on the dark calendar.
      color: vividForDarkBackground(
        generateHexFromString(event.employeeColor || Default_Instructor_Color)
      ),
    });
  });

  // The map keeps that first-class order. The "no instructor" dot goes last;
  // whatever doesn't fit goes under "+N".
  const ordered = Array.from(dotsByEmployee.values());
  if (hasUnassigned) {
    ordered.push({
      employeeId: null,
      name: UNASSIGNED_DOT_NAME,
      color: UNASSIGNED_DOT_COLOR,
    });
  }

  return {
    dots: ordered.slice(0, MAX_INSTRUCTOR_DOTS),
    hidden: ordered.slice(MAX_INSTRUCTOR_DOTS),
  };
};

export const createTileContentFunction = (
  allEvents: IPlannerDayModel[],
  filters: IPlannerFiltersState
) => {
  // The same day can arrive in more than one cached month response; merge them.
  // A dot stands for an instructor, not a class, so a repeated class changes nothing.
  const eventsByDay = new Map<string, IPlannerEventModel[]>();
  allEvents?.forEach((day) => {
    const dayKey = dateUtility.formatDateForHref(new Date(day.date))!;
    eventsByDay.set(dayKey, [...(eventsByDay.get(dayKey) ?? []), ...(day.events ?? [])]);
  });

  const dotsByDay = new Map<string, IInstructorDotsForDay>();
  eventsByDay.forEach((dayEvents, dayKey) => {
    dotsByDay.set(
      dayKey,
      getInstructorDotsForDay(filterEventsByFilters(dayEvents, filters))
    );
  });

  return ({ date, view }: TileContentProps): React.ReactNode => {
    if (view !== "month") {
      return null;
    }

    const day = dotsByDay.get(dateUtility.formatDateForHref(new Date(date))!);
    if (!day?.dots.length) {
      return null;
    }

    return (
      <span className={styles.calendar_instructor_dots}>
        {day.dots.map((dot) => (
          <span
            key={dot.employeeId ?? "unassigned"}
            className={styles.calendar_instructor_dot}
            style={{ color: dot.color }}
            title={dot.name}
          />
        ))}
        {day.hidden.length > 0 && (
          <span
            className={styles.calendar_instructor_more}
            title={day.hidden.map((x) => x.name).join(", ")}
          >
            +{day.hidden.length}
          </span>
        )}
      </span>
    );
  };
};
